import { bindable, BindingMode, customElement } from 'aurelia';
import template from './form-tag-select.html';
import type { Tag } from '../services/api.types';

let nextInstanceId = 0;

@customElement({ name: 'form-tag-select', template })
export class FormTagSelect {
  @bindable({ mode: BindingMode.twoWay }) public value = '';
  @bindable public tags: Tag[] = [];
  @bindable public placeholder = '';
  @bindable public disabled = false;
  @bindable public controlId = '';
  @bindable public valueKey: 'id' | 'name' = 'id';

  public isOpen = false;
  public highlightedIndex = -1;
  public readonly listId = `form-tag-select-options-${++nextInstanceId}`;

  public get sortedTags(): Tag[] {
    return this.tags.slice().sort((a, b) => a.position - b.position);
  }

  public get selectedTag(): Tag | undefined {
    if (!this.value) return undefined;

    return this.tags.find(tag => tag.id === this.value)
      ?? this.tags.find(tag => tag.name === this.value)
      ?? this.tags.find(tag =>
        tag.name.toLocaleLowerCase() === this.value.toLocaleLowerCase());
  }

  public get activeOptionId(): string {
    return this.highlightedIndex >= 0
      ? `${this.listId}-option-${this.highlightedIndex}`
      : '';
  }

  public toggleDropdown(): void {
    if (this.disabled || this.sortedTags.length === 0) return;

    this.isOpen = !this.isOpen;
    this.highlightedIndex = -1;
  }

  public selectTag(tag: Tag): void {
    if (this.disabled) return;

    this.value = this.valueKey === 'name' ? tag.name : tag.id;
    this.isOpen = false;
    this.highlightedIndex = -1;
  }

  public onControlKeydown(event: KeyboardEvent): void {
    if (this.disabled) return;

    const tags = this.sortedTags;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (tags.length === 0) return;

      event.preventDefault();
      this.isOpen = true;
      if (event.key === 'ArrowDown') {
        this.highlightedIndex = this.highlightedIndex < 0 ||
            this.highlightedIndex >= tags.length - 1
          ? 0
          : this.highlightedIndex + 1;
      } else {
        this.highlightedIndex = this.highlightedIndex <= 0 ||
            this.highlightedIndex >= tags.length
          ? tags.length - 1
          : this.highlightedIndex - 1;
      }
      return;
    }

    if ((event.key === 'Enter' || event.key === ' ') && this.isOpen &&
        this.highlightedIndex >= 0) {
      const tag = tags[this.highlightedIndex];
      if (tag) {
        event.preventDefault();
        this.selectTag(tag);
      }
      return;
    }

    if (event.key === 'Escape' && this.isOpen) {
      event.preventDefault();
      this.isOpen = false;
      this.highlightedIndex = -1;
    }
  }
}
